import { createColumnHelper } from "@tanstack/react-table";
import clsx from "clsx";
import {
  BrazilianCompanyIssuerFreeFloat,
  BrazilianCompanyIssuerListingSegment,
  BrazilianCompanyIssuerTagAlong,
  BrazilianCompanyMajorityShareholders,
} from "components/company/v2";
import Table from "components/ui/Table";
import { TableBreakpoint } from "components/ui/Table/interface";
import { Tab, TabList, TabPanel, TabPanels, Tabs } from "components/ui/Tabs";
import conditionalCounters from "components/utils/conditionalCounters";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import { Dialog, DialogCloseX, palette, shades } from "lib/miolo";
import mediaQueries from "lib/utils/layout/mediaQueries";
import { orderBy, trimEnd } from "lodash";
import numeral from "numeral";
import type { ReactNode } from "react";
import { useState } from "react";
import tinycolor from "tinycolor2";
import type { IBrazilianCompanyIssuerTreasuryShares } from "types/graphqlTypes";
import { IBrazilianCompanyIssuerShareholderType } from "types/graphqlTypes";

function Title({ children }: { children: ReactNode }) {
  return <h6 className="flex font-aeonik text-sm font-semibold uppercase tracking-wide text-slate-600">{children}</h6>;
}

interface IBrazilianCompanyIssuerGovernanceProps {
  issuer: IBrazilianCompanyIssuerTopLevelFragment;
}

function BrazilianCompanyIssuerGovernance({ issuer }: IBrazilianCompanyIssuerGovernanceProps) {
  const [showAllShareholders, setShowAllShareholders] = useState(false);

  return (
    <div className="space-y-2 rounded-xl bg-slate-50 p-6 font-aeonik">
      <div className="space-y-6">
        {issuer.chiefExecutiveOfficer && (
          <div className="space-y-2">
            <Title>CEO</Title>
            <span className="flex text-sm font-medium text-gray-500 xl:text-base">
              {issuer.chiefExecutiveOfficer.name}
            </span>
          </div>
        )}

        <div className="flex flex-col justify-between space-y-6 xl:flex-row xl:space-y-0">
          {issuer.listingSegment && (
            <div className="space-y-2">
              <Title>Segmento de Listagem</Title>
              <BrazilianCompanyIssuerListingSegment listingSegment={issuer.listingSegment} />
            </div>
          )}

          {issuer.tagAlong && (
            <div className="space-y-2">
              <Title>Tag Along</Title>
              <BrazilianCompanyIssuerTagAlong tagAlong={issuer.tagAlong} />
            </div>
          )}

          {issuer.freeFloat && (
            <div className="space-y-2">
              <Title>Free Float</Title>
              <BrazilianCompanyIssuerFreeFloat freeFloat={issuer.freeFloat} />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Title>{issuer.majorityShareholders.length === 1 ? "Sócio Majoritário" : "Sócios Majoritários"}</Title>

          <BrazilianCompanyMajorityShareholders majorityShareholders={issuer.majorityShareholders} />

          {showAllShareholders && (
            <AllShareholdersDialog
              isOpen={showAllShareholders}
              onDismiss={() => setShowAllShareholders(false)}
              positionOfShareholders={issuer.positionOfShareholders}
              treasuryShares={issuer.treasuryShares}
              ownershipStructureUrl={issuer.ownershipStructureUrl}
            />
          )}

          <div className="hidden pt-2 xl:block">
            <button
              type="button"
              onClick={() => setShowAllShareholders(true)}
              className="font-aeonik text-sm font-bold text-blue-600 hover:underline"
            >
              Ver todos os sócios ({issuer.positionOfShareholders.length}) →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BrazilianCompanyIssuerGovernance;

interface IAllShareholdersDialogProps {
  ownershipStructureUrl: IBrazilianCompanyIssuerTopLevelFragment["ownershipStructureUrl"];
  positionOfShareholders: IBrazilianCompanyIssuerTopLevelFragment["positionOfShareholders"];
  treasuryShares: IBrazilianCompanyIssuerTreasuryShares | null;
  isOpen: boolean;
  onDismiss: () => void;
}

function AllShareholdersDialog({
  ownershipStructureUrl,
  positionOfShareholders,
  treasuryShares,
  isOpen,
  onDismiss,
}: IAllShareholdersDialogProps) {
  const [isShowingAllShareholders, setShowAllShareholders] = useState(false);

  const treasurySharesColumns = getTreasurySharesColumns(treasuryShares);
  const hasTreasuryShares = treasuryShares && treasuryShares.totalShares > 0;

  const shareholdersCount = conditionalCounters(positionOfShareholders, {
    individualShareholders: ({ type }) => type === IBrazilianCompanyIssuerShareholderType.Individual,
    corporationShareholders: ({ type }) => type === IBrazilianCompanyIssuerShareholderType.Corporation,
    otherShareholders: ({ type }) => type === IBrazilianCompanyIssuerShareholderType.Other,
  });

  const minorityShareholdersCount = positionOfShareholders.filter((node) => {
    return node.percentageOfTotalShares < 0.5;
  }).length;

  const shareholders = positionOfShareholders.filter((node) => {
    if (isShowingAllShareholders) {
      return true;
    }
    return node.percentageOfTotalShares > 0.5;
  });

  return (
    <Dialog
      aria-label="Incluir Ativo na Carteira"
      isOpen={isOpen}
      onDismiss={onDismiss}
      allowPinchZoom={true}
      css={mediaQueries({
        position: "relative",
        display: "block",
        boxSizing: "border-box",
        minWidth: 320,
        maxWidth: 1140,
        margin: [12, "10vh auto 10vh auto", "10vh auto 10vh auto", "10vh auto"],
        padding: [18, 18, 18, 24],
        borderRadius: [6, 6, 6, 12],
      })}
    >
      <div css={{ display: "grid", gridRowGap: 20 }}>
        <header>
          <div
            css={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <h1
              css={{
                margin: 0,
                color: palette.bigStone,
                fontWeight: "bold",
                fontSize: 28,
                fontFamily: "museo-sans-condensed",
                lineHeight: 1.33,
                letterSpacing: 1,
              }}
            >
              Sócios
            </h1>

            <DialogCloseX onClick={onDismiss} />
          </div>

          <p
            css={{
              margin: 0,
              marginTop: 4,
              marginBottom: 12,
              color: palette.lynch,
              fontWeight: 500,
              fontSize: 14,
              fontFamily: "museo-sans-condensed",
              lineHeight: 1.33,
              letterSpacing: 1,
            }}
          >
            Relação com todos os sócios reportados pela empresa na CVM.
          </p>

          <a
            target="_blank"
            rel="noopener noreferrer"
            href={ownershipStructureUrl}
            css={{
              textDecoration: "none",
              color: palette.link,
              fontWeight: 500,
              fontSize: 14,
              fontFamily: "museo-sans-condensed",
              lineHeight: 1.33,
              letterSpacing: 1,
              "&:hover": {
                textDecoration: undefined,
              },
            }}
          >
            Ver organograma dos acionistas →
          </a>
        </header>

        <div css={{ marginLeft: -24, marginRight: -24 }}>
          <hr
            css={{
              margin: 0,
              border: "none",
              borderTop: `solid 1px ${palette.catskillWhite}`,
            }}
          />

          <div css={{ padding: 12 }}>
            <Tabs>
              <TabList>
                <Tab>Total ({positionOfShareholders.length})</Tab>
                <Tab disabled={shareholdersCount.individualShareholders === 0}>
                  Pessoa Física ({shareholdersCount.individualShareholders})
                </Tab>
                <Tab disabled={shareholdersCount.corporationShareholders === 0}>
                  Pessoa Jurídica ({shareholdersCount.corporationShareholders})
                </Tab>
                <Tab disabled={shareholdersCount.otherShareholders === 0}>
                  Outros ({shareholdersCount.otherShareholders})
                </Tab>
                {hasTreasuryShares && <Tab>Ações em Tesouraria</Tab>}
              </TabList>

              <TabPanels>
                <TabPanel>
                  <ShareholdersTable shareholders={shareholders} />

                  {minorityShareholdersCount > 0 && (
                    <div css={{ textAlign: "center", padding: "12px 0px" }}>
                      <button
                        type="button"
                        onClick={() => setShowAllShareholders(!isShowingAllShareholders)}
                        css={{
                          margin: 0,
                          padding: 0,
                          color: palette.mariner,
                          fontSize: 14,
                          letterSpacing: 1,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          appearance: "none",
                        }}
                      >
                        {isShowingAllShareholders
                          ? `Recolher sócios minoritários`
                          : `Ver os demais sócios (${minorityShareholdersCount})`}
                      </button>
                    </div>
                  )}
                </TabPanel>
                <TabPanel>
                  <ShareholdersTable
                    shareholders={positionOfShareholders.filter(({ type }) => {
                      return type === IBrazilianCompanyIssuerShareholderType.Individual;
                    })}
                  />
                </TabPanel>
                <TabPanel>
                  <ShareholdersTable
                    shareholders={positionOfShareholders.filter(({ type }) => {
                      return type === IBrazilianCompanyIssuerShareholderType.Corporation;
                    })}
                  />
                </TabPanel>
                <TabPanel>
                  <ShareholdersTable
                    shareholders={positionOfShareholders.filter(({ type }) => {
                      return type === IBrazilianCompanyIssuerShareholderType.Other;
                    })}
                  />
                </TabPanel>
                <TabPanel>
                  <Table<IBrazilianCompanyIssuerTreasuryShares>
                    columns={treasurySharesColumns}
                    data={treasuryShares ? [treasuryShares] : []}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

interface IShareholdersTableProps {
  shareholders: IBrazilianCompanyIssuerTopLevelFragment["positionOfShareholders"];
}

function ShareholdersTable({ shareholders }: IShareholdersTableProps) {
  const hasShareholderWithPreferredStock = shareholders.some((shareholder) => {
    return shareholder.percentageOfTotalSharesOfPreferredStock > 0;
  });

  return (
    <div
      css={{
        overflow: "hidden",
        backgroundColor: palette.white,
        borderRadius: 4,
      }}
    >
      <table
        css={{
          minWidth: "100%",
          fontSize: 14,
          fontFamily: "museo-sans-condensed",
          lineHeight: 1.33,
          letterSpacing: 0.6,
          textAlign: "left",
          textIndent: 0,
          borderColor: "inherit",
          borderCollapse: "collapse",
          "& > thead": {
            backgroundColor: palette.white,
          },
          "& th": {
            padding: "12px 16px",
            color: shades.greys.variants[2],
            fontWeight: 600,
            textTransform: "uppercase",
          },
          "& tr > td": {
            padding: "12px 16px",
          },
          "& > tbody": {
            "& tr": {
              borderColor: palette.fiord,
              borderTopWidth: 1,
              "&:hover": {
                backgroundColor: tinycolor(palette.catskillWhite).lighten(2).toHexString(),
                "& td:first-of-type": {
                  borderTopLeftRadius: 6,
                  borderBottomLeftRadius: 6,
                },
                "& td:last-of-type": {
                  borderTopRightRadius: 6,
                  borderBottomRightRadius: 6,
                },
              },
            },
          },
          "& tbody > tr": {
            borderTop: `solid 1px ${tinycolor(palette.mystic).lighten(3).toHexString()}`,
            borderBottom: "none",
            "&:hover, &:hover + tr": {
              borderTop: "solid 1px transparent",
            },
          },
        }}
      >
        <thead>
          <tr>
            <th>Nome</th>
            <th css={{ textAlign: "right" }}>% ON</th>
            {hasShareholderWithPreferredStock && <th css={{ textAlign: "right" }}>% PN</th>}
          </tr>
        </thead>

        <tbody>
          {orderBy(shareholders, ["percentageOfTotalShares"], ["desc"]).map((node, index) => {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`${node.name}/${index}`}>
                <td>
                  <div css={{ display: "flex", alignItems: "center" }}>
                    <div css={{ width: 280 }}>
                      <div>
                        <span
                          css={{
                            fontSize: 16,
                            fontWeight: 500,
                            color: shades.greys.variants[2],
                          }}
                        >
                          {node.name}
                        </span>
                      </div>

                      <div>
                        <span css={{ fontSize: 14, color: shades.greys.variants[3], letterSpacing: 1 }}>
                          {"Nacionalidade: "}
                          <strong style={{ color: shades.greys.variants[4] }}>{node.nationality}</strong>
                        </span>
                      </div>
                    </div>

                    {node.isControllingShareholder && (
                      <div css={{ marginLeft: 12 }}>
                        <span
                          css={{
                            padding: "4px 12px",
                            color: palette.white,
                            fontWeight: 500,
                            letterSpacing: 1,
                            backgroundColor: palette.mineShaft,
                            borderRadius: 12,
                          }}
                        >
                          Controlador
                        </span>
                      </div>
                    )}

                    {node.isGovernmentOrGovernmentRelated && (
                      <div css={{ marginLeft: 12 }}>
                        <span
                          css={{
                            padding: "4px 12px",
                            fontWeight: 500,
                            letterSpacing: 1,
                            color: palette.crownOfThorns,
                            backgroundColor: palette.vanillaIce,
                            borderRadius: 12,
                          }}
                        >
                          Governo
                        </span>
                      </div>
                    )}
                  </div>
                </td>

                <td css={{ textAlign: "right" }}>
                  <div>
                    <span
                      css={{
                        color: palette.oceanGreen,
                        fontWeight: 700,
                        fontSize: 16,
                      }}
                    >
                      {node.percentageOfTotalSharesOfCommonStock.toFixed(2).replace(".00", "").replace(".", ",")}%
                    </span>
                  </div>

                  {node.percentageOfTotalSharesOfCommonStock > 0.5 && (
                    <div>
                      <span
                        css={{
                          color: shades.greys.variants[4],
                          fontWeight: 500,
                          fontSize: 12,
                          letterSpacing: 1,
                        }}
                      >
                        {numeral(node.totalSharesOfCommonStock).format("0,0")} ações
                      </span>
                    </div>
                  )}
                </td>

                {hasShareholderWithPreferredStock && (
                  <td css={{ textAlign: "right" }}>
                    <div>
                      <span
                        css={{
                          color: palette.copper,
                          fontWeight: 700,
                          fontSize: 16,
                        }}
                      >
                        {node.percentageOfTotalSharesOfPreferredStock.toFixed(2).replace(".00", "").replace(".", ",")}%
                      </span>
                    </div>

                    {node.percentageOfTotalSharesOfPreferredStock > 0.5 && (
                      <div>
                        <span
                          css={{
                            color: shades.greys.variants[4],
                            fontWeight: 500,
                            fontSize: 12,
                            letterSpacing: 1,
                          }}
                        >
                          {numeral(node.totalSharesOfPreferredStock).format("0,0")} ações
                        </span>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface ITreasurySharesCellProps {
  percentageOfTotal: number;
  shares: number;
}

function TreasurySharesCell({ percentageOfTotal, shares }: ITreasurySharesCellProps) {
  return (
    <div>
      <div>
        <span className="text-base font-bold text-emerald-600">
          {percentageOfTotal < 0.01
            ? trimEnd(String(percentageOfTotal).replace(".", ","))
            : percentageOfTotal.toFixed(2).replace(".00", "").replace(".", ",")}
          %
        </span>
      </div>

      <div>
        <span className="text-medium text-xs tracking-wider text-gray-600">{numeral(shares).format("0,0")} ações</span>
      </div>
    </div>
  );
}

function getTreasurySharesColumns(treasuryShares: IBrazilianCompanyIssuerTreasuryShares | null) {
  if (!treasuryShares) {
    return [];
  }

  const columnHelper = createColumnHelper<IBrazilianCompanyIssuerTreasuryShares>();
  const hiddenPreferredStock = !treasuryShares.totalSharesOfPreferredStock;
  const hiddenCommonStock = !treasuryShares.totalSharesOfCommonStock;

  const headerClassName = clsx(
    "border-b border-gray-200 py-3 px-4",
    "text-left text-sm font-semibold uppercase tracking-wide text-gray-700",
  );
  const cellClassName = clsx("text-left text-sm tracking-wide text-gray-700");

  const arrayOfColumns = [
    columnHelper.accessor("totalSharesOfCommonStock", {
      header: "% ON",
      cell: (props) => (
        <TreasurySharesCell
          percentageOfTotal={props.row.original.percentageOfTotalSharesOfCommonStock}
          shares={props.getValue()}
        />
      ),
      meta: {
        isHidden: hiddenCommonStock,
        header: {
          className: headerClassName,
        },
        cell: {
          className: cellClassName,
        },
      },
    }),
    columnHelper.accessor("totalSharesOfPreferredStock", {
      header: "% PN",
      cell: (props) => (
        <TreasurySharesCell
          percentageOfTotal={props.row.original.percentageOfTotalSharesOfPreferredStock}
          shares={props.getValue()}
        />
      ),
      meta: {
        isHidden: hiddenPreferredStock,
        breakpoint: TableBreakpoint.Small,
        header: {
          className: headerClassName,
        },
        cell: {
          className: cellClassName,
        },
      },
    }),
    columnHelper.accessor("totalShares", {
      header: "TOTAL",
      cell: (props) => (
        <TreasurySharesCell percentageOfTotal={props.row.original.percentageOfTotalShares} shares={props.getValue()} />
      ),
      meta: {
        breakpoint: TableBreakpoint.Small,
        header: {
          className: headerClassName,
        },
        cell: {
          className: cellClassName,
        },
      },
    }),
  ];

  return arrayOfColumns;
}
